import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from "react";
import { useRecordContext, useRefresh, useTranslate } from "react-admin";
// import { useParams } from "react-router-dom";
import { muiColors } from "themes/colors";
import {
  CancelledIcon,
  EditButtonIcon,
  RefundedOrderButtonIcon
} from "assets/Icons";
import { ListItemButton, Popover } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { CustomButton } from "./CustomButton";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import * as User from "../providers/user";
import * as Cognito from "../providers/cognito";
import * as Axios from "../providers/axios";
interface OrderDetailsCustomButtonsProps {
  openChargeModal: () => void;
  setWhatToAdd: Dispatch<
    SetStateAction<"SERVICE" | "ADDON" | "FREEFORM" | "DISCOUNT" | null>
  >;
  setIsConfirmationModalOpen: Dispatch<SetStateAction<string | null>>;
  setCancelModalAppointmentId: Dispatch<SetStateAction<string | null>>;
  openAddArbitaryCreditsModal: () => void;
  openEditModal: (() => void) | null;
  openAddUserModal: () => void;
}

const OrderDetailsCustomButtons = ({
  openChargeModal,
  setCancelModalAppointmentId,
  setWhatToAdd,
  openAddArbitaryCreditsModal,
  openEditModal,
  openAddUserModal
}: OrderDetailsCustomButtonsProps) => {
  const { permissions } = User.useUser();
  const translate = useTranslate();
  const { user } = User.useUser();
  const { user: cognitoUser } = Cognito.useCognito();
  const { api } = Axios.useAxios();
  const refresh = useRefresh();
  const record = useRecordContext();
  const { id } = useParams<{ id: string }>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  function handleIsWebview() {
    if (globalThis.isReactNativeWebview === "true") {
      return true;
    }
    if (globalThis.isReactNativeWebview2 === "yes") {
      return true;
    }
    return false;
  }

  const handlePosClick = () => {
    if (handleIsWebview()) {
      const action = {
        type: "stripe.terminal.payment",
        payload: {
          orderId: id,
          company_id: user.impersonate_company_id || user.company.id,
          accessToken: cognitoUser?.access_token
        }
      };
      // @ts-ignore
      window?.ReactNativeWebView?.postMessage(JSON.stringify(action));
    }
  };

  const handleMessage = useCallback(
    (event: any) => {
      if (
        event &&
        event?.data &&
        event?.origin !== window.location.origin &&
        event?.data?.action !== "card-element-link-report" &&
        event?.data?.action !== "card-element-link-load"
      ) {
        let messageObject: any = null;

        // In RN WebView, `event.data` is usually a JSON string.
        // On regular web pages (including Stripe/CardElement), `event.data` can be an object.
        if (typeof event?.data === "string") {
          try {
            messageObject = JSON.parse(event.data);
          } catch (err) {
            // Ignore non-JSON strings (prevents: "[object Object]" is not valid JSON)
          }
        } else if (event?.data && typeof event.data === "object") {
          messageObject = event.data;
        }

        // Ignore Stripe/CardElement/hCaptcha internal postMessage traffic
        // const action = messageObject?.action || messageObject?.message?.action;
        // if (
        //   messageObject?.__stripeJsV3 ||
        //   messageObject?.sourceFrameId?.includes("__privateStripe") ||
        //   messageObject?.controllerAppFrameId?.includes("__privateStripe") ||
        //   messageObject?.originatingScript === "m2" ||
        //   messageObject?.channel === "hcaptcha-invisible" ||
        //   (typeof action === "string" && action.startsWith("stripe-"))
        // ) {
        //   return;
        // }

        // if (!messageObject) return;

        if (messageObject.action === "SEND_PAYMENT_INTENT_MESSAGE") {
          const paymentIntentId = messageObject.paymentIntentId;
          const orderId = messageObject.orderId;
          api
            .post(`/v1/payment-intent`, {
              paymentIntentId,
              orderId,
              sub: cognitoUser?.id
            })
            .then((response) => {
              refresh();
            })
            .catch((error) => alert(error));
        }
      }
    },
    [api, cognitoUser?.id, refresh]
  );

  useEffect(() => {
    // For Android
    document.addEventListener("message", handleMessage);
    // For IOS
    window.addEventListener("message", handleMessage);
    return () => {
      document.removeEventListener("message", handleMessage);
      window.removeEventListener("message", handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    setCancelModalAppointmentId(record?.appointmentId);
    setAnchorEl(null);
  };

  const isPopoverOpen = Boolean(anchorEl);
  const popoverId = isPopoverOpen ? "order-details-more" : undefined;

  return (
    <div className="flex items-center gap-3 sm:gap-2 flex-wrap w-full sm:w-auto">
      {record?.payment_status !== "CANCELLED" &&
        record?.payment_status !== "REFUNDED" &&
        record?.location?.company?.stripe_user_id &&
        handleIsWebview() && (
          <CustomButton
            text="POS"
            disabled={!record.id}
            onClick={handlePosClick}
            cyId="pos-charge-button"
            className="w-full sm:w-auto"
          />
        )}
      {record?.payment_status !== "CANCELLED" &&
        record?.payment_status !== "REFUNDED" &&
        record?.location?.company?.stripe_user_id && (
          <CustomButton
            text={translate("order.charge")}
            disabled={!record.id}
            onClick={
              !record?.user?.first_name ? openAddUserModal : openChargeModal
            }
            cyId="charge-button"
            className="w-full sm:w-auto"
          />
        )}
      {!permissions?.isArtist &&
        record?.payment_status !== "CANCELLED" &&
        record?.payment_status !== "REFUNDED" &&
        openEditModal && (
          <CustomButton
            text={translate("order.edit_order")}
            variant="secondary"
            startIcon={<EditButtonIcon />}
            onClick={openEditModal}
            className="w-full sm:w-auto"
          />
        )}
      {record?.payment_status !== "CANCELLED" &&
        record?.payment_status !== "REFUNDED" && (
          <>
            <CustomButton
              text={translate("order.add_service")}
              variant="secondary"
              startIcon={<AddIcon />}
              onClick={() => setWhatToAdd("SERVICE")}
              cyId="add-service-to-order-button"
              className="w-full sm:w-auto"
            />
            <CustomButton
              text={translate("order.add_add_on")}
              variant="secondary"
              startIcon={<AddIcon />}
              onClick={() => setWhatToAdd("ADDON")}
              cyId="add-addon-to-order-button"
              className="w-full sm:w-auto"
            />
            <CustomButton
              text={translate("order.add_freeform")}
              variant="secondary"
              startIcon={<AddIcon />}
              onClick={() => setWhatToAdd("FREEFORM")}
              cyId="add-freeform-to-order-button"
              className="w-full sm:w-auto"
            />
            <CustomButton
              text={translate("order.add_discount")}
              variant="secondary"
              startIcon={<AddIcon />}
              onClick={() => setWhatToAdd("DISCOUNT")}
              cyId="add-discount-to-order-button"
              className="w-full sm:w-auto"
            />
            <CustomButton
              text={translate("order.add_arbitary_credits")}
              variant="secondary"
              startIcon={<AddIcon />}
              onClick={openAddArbitaryCreditsModal}
              cyId="add-credits-button"
              className="w-full sm:w-auto"
            />
          </>
        )}
      {record?.payment_status === "CANCELLED" && (
        <CustomButton
          text={translate("order.cancelled")}
          variant="secondary"
          sx={{
            border: `dashed 1px ${muiColors.button.disabled}`,
            "&:disabled": {
              color: muiColors.error,
              svg: {
                fill: muiColors.error,
                fillOpacity: 1,
                path: {
                  fill: muiColors.error,
                  fillOpacity: 1
                }
              }
            }
          }}
          startIcon={<CancelledIcon />}
          disabled={true}
          className="w-full sm:w-auto"
        />
      )}
      {record?.payment_status === "REFUNDED" && (
        <CustomButton
          text={translate("order.refunded")}
          variant="secondary"
          sx={{
            border: `dashed 1px ${muiColors.button.disabled}`,
            "&:disabled": {
              color: muiColors.warning,
              svg: {
                fill: muiColors.warning,
                fillOpacity: 1,
                path: {
                  fill: muiColors.warning,
                  fillOpacity: 1
                }
              }
            }
          }}
          startIcon={<RefundedOrderButtonIcon />}
          disabled={true}
          className="w-full sm:w-auto"
        />
      )}
      {record?.payment_status !== "REFUNDED" &&
        record?.payment_status !== "CANCELLED" &&
        record?.appointmentId && (
          <CustomButton
            onClick={(event: MouseEvent<HTMLButtonElement>): void => {
              setAnchorEl(event?.currentTarget);
            }}
            text={translate("common.more")}
            cyId="order_three_dots_button"
            variant="secondary"
            startIcon={<MoreHorizIcon />}
            className="w-full sm:w-auto"
          />
        )}
      <Popover
        id={popoverId}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        elevation={4}
        sx={{
          "& .MuiPopover-paper": {
            backgroundColor: "#fff",
            borderRadius: "4px"
          }
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: -8,
          horizontal: "right"
        }}
      >
        {/* {record?.payment_status !== "FULLY_PAID" && (
          <ListItemButton
            onClick={handleExportPdf}
            sx={{
              fontSize: "14px",
              fontFamily: "Inter",
              width: "200px",
              "& .MuiListItemButton-root": {
                fontSize: "14px",
                fontFamily: "Inter"
              }
            }}
          >
            {translate("order.export_to_pdf")}
          </ListItemButton>
        )} */}
        {/* <ListItemButton
          onClick={handleRefund}
          sx={{
            fontSize: "14px",
            fontFamily: "Inter",
            "& .MuiListItemButton-root": {
              fontSize: "14px",
              fontFamily: "Inter"
            }
          }}
        >
          {translate("order.refund")}
        </ListItemButton> */}
        {record?.payment_status !== "CANCELLED" && (
          <ListItemButton
            onClick={handleCancel}
            data-cy="cancel-appointment-button"
            sx={{
              fontSize: "14px",
              fontFamily: "Inter",
              color: muiColors.error,
              "& .MuiListItemButton-root": {
                fontSize: "14px",
                fontFamily: "Inter"
              }
            }}
          >
            {translate("order.cancel")}
          </ListItemButton>
        )}
      </Popover>
    </div>
  );
};
export default OrderDetailsCustomButtons;
